import { useState, useEffect } from 'react';
import { headlessGet, headlessPost, apiPost } from '../../lib/api';
import { getToken } from '../../lib/auth';

// ─── Types ───────────────────────────────────────────────────────────────────

interface PollOption {
  id: string;
  text: string;
  sortOrder: number;
}

interface PollQuestion {
  id: string;
  text: string;
  type: 'single_choice' | 'multiple_choice';
  options: PollOption[];
}

interface Poll {
  id: string;
  title: string;
  description?: string | null;
  questions: PollQuestion[];
  completionPoints?: number | null;
  hasResponded?: boolean;
}

interface PollResultOption {
  id: string;
  text: string;
  count: number;
  percent: number;
}

interface PollResults {
  totalResponses: number;
  questions: Array<{
    id: string;
    text: string;
    options: PollResultOption[];
  }>;
}

interface PollBlockProps {
  questionnaireId?: string;
}

// ─── Spinner ─────────────────────────────────────────────────────────────────

function Spinner() {
  return (
    <svg className="w-5 h-5 animate-spin text-[var(--color-primary,#4F46E5)]" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z" />
    </svg>
  );
}

// ─── Results bar chart ────────────────────────────────────────────────────────

function ResultsView({ results, totalLabel }: { results: PollResults; totalLabel?: string }) {
  return (
    <div className="space-y-6">
      {results.questions.map((q) => (
        <div key={q.id}>
          <p className="text-sm font-semibold text-[var(--color-text,#111)] mb-3">{q.text}</p>
          <div className="space-y-2">
            {q.options.map((opt) => (
              <div key={opt.id}>
                <div className="flex justify-between text-xs text-[var(--color-text,#111)] mb-1">
                  <span>{opt.text}</span>
                  <span className="font-medium">{opt.percent}%</span>
                </div>
                <div className="h-2 rounded-full bg-[var(--color-bg-muted,#f3f4f6)] overflow-hidden">
                  <div
                    className="h-full rounded-full bg-[var(--color-primary,#4F46E5)] transition-all duration-700"
                    style={{ width: `${opt.percent}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
      <p className="text-xs text-[var(--color-text-muted,#6b7280)]">
        {totalLabel ?? `${results.totalResponses} vote${results.totalResponses !== 1 ? 's' : ''} total`}
      </p>
    </div>
  );
}

// ─── Block ────────────────────────────────────────────────────────────────────

export function PollBlock({ props }: { props: PollBlockProps }) {
  const { questionnaireId } = props;
  const isAuthenticated = !!getToken();

  const [poll, setPoll] = useState<Poll | null>(null);
  const [results, setResults] = useState<PollResults | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [voted, setVoted] = useState(false);

  // answers keyed by questionId
  const [answers, setAnswers] = useState<Record<string, string[]>>({});

  useEffect(() => {
    if (!questionnaireId) return;
    setLoading(true);
    headlessGet<Poll>(`/questionnaires/${questionnaireId}`)
      .then((data) => {
        setPoll(data);
        if (data.hasResponded) {
          setVoted(true);
          return headlessGet<PollResults>(`/questionnaires/${questionnaireId}/results`);
        }
        return null;
      })
      .then((res) => {
        if (res) setResults(res);
      })
      .catch((err) => {
        setError((err as Error).message ?? 'Failed to load poll');
      })
      .finally(() => setLoading(false));
  }, [questionnaireId]);

  function toggleAnswer(questionId: string, optionId: string, multi: boolean) {
    setAnswers((prev) => {
      const current = prev[questionId] ?? [];
      if (multi) {
        return {
          ...prev,
          [questionId]: current.includes(optionId)
            ? current.filter((id) => id !== optionId)
            : [...current, optionId],
        };
      }
      return { ...prev, [questionId]: [optionId] };
    });
  }

  async function handleVote() {
    if (!poll) return;
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        answers: Object.entries(answers).map(([questionId, optionIds]) => ({
          questionId,
          optionIds,
        })),
      };

      if (isAuthenticated) {
        await apiPost(`/customer/questionnaires/${questionnaireId}/respond`, payload);
      } else {
        await headlessPost(`/questionnaires/${questionnaireId}/vote`, payload);
      }

      const res = await headlessGet<PollResults>(`/questionnaires/${questionnaireId}/results`);
      setResults(res);
      setVoted(true);
    } catch (err) {
      const errData = (err as Error & { data?: { error?: string } }).data;
      setError(errData?.error ?? (err instanceof Error ? err.message : 'Failed to submit vote'));
    } finally {
      setSubmitting(false);
    }
  }

  // ── Render states ──────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="py-8 px-4">
        <div className="max-w-lg mx-auto flex justify-center py-10">
          <Spinner />
        </div>
      </div>
    );
  }

  if (error && !poll) {
    return (
      <div className="py-8 px-4">
        <div className="max-w-lg mx-auto text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
          {error}
        </div>
      </div>
    );
  }

  if (!poll) return null;

  return (
    <div className="py-8 px-4">
      <div className="max-w-lg mx-auto rounded-xl border border-[var(--color-border,#e5e7eb)] bg-[var(--color-bg,#fff)] p-6 shadow-sm">
        {/* Header */}
        <h3 className="text-base font-bold text-[var(--color-text,#111)] mb-1">{poll.title}</h3>
        {poll.description && (
          <p className="text-sm text-[var(--color-text-muted,#6b7280)] mb-4">{poll.description}</p>
        )}

        {/* Already voted — show results */}
        {voted && results ? (
          <>
            <div className="mb-4 inline-flex items-center gap-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-3 py-1">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
              Already voted
            </div>
            <ResultsView results={results} />
          </>
        ) : (
          // Voting form
          <div className="space-y-5">
            {poll.questions.map((q) => {
              const isMulti = q.type === 'multiple_choice';
              const selected = answers[q.id] ?? [];
              return (
                <div key={q.id}>
                  <p className="text-sm font-semibold text-[var(--color-text,#111)] mb-2">{q.text}</p>
                  <div className="space-y-2">
                    {q.options.map((opt) => {
                      const checked = selected.includes(opt.id);
                      return (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => toggleAnswer(q.id, opt.id, isMulti)}
                          className={`w-full text-left px-4 py-2.5 rounded-lg border text-sm transition-all duration-150 ${
                            checked
                              ? 'border-[var(--color-primary,#4F46E5)] bg-[var(--color-primary,#4F46E5)]/10 font-medium text-[var(--color-primary,#4F46E5)]'
                              : 'border-[var(--color-border,#e5e7eb)] text-[var(--color-text,#111)] hover:border-[var(--color-primary,#4F46E5)] hover:bg-[var(--color-primary,#4F46E5)]/5'
                          }`}
                        >
                          {opt.text}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {error && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                {error}
              </p>
            )}

            <button
              type="button"
              onClick={handleVote}
              disabled={submitting || Object.keys(answers).length === 0}
              className="w-full py-2.5 rounded-lg bg-[var(--color-primary,#4F46E5)] text-white text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting && <Spinner />}
              Vote
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
